import type { RealtimeFrame } from '@/features/shared/use-realtime-stream';
import { CERTIFICATION_STAGES } from './api';
import type {
  AvanceDeclaration,
  AvanceRow,
  AvanceRowKind,
  AvanceStepAdvance,
  CertificationStage,
  SiiAvanceView,
} from './api';

/**
 * The pure rules the «Avance SII» screen decides with.
 *
 * The SERVER is the authority: `POST …/declarations` recomputes the rows and
 * answers 409/422 when they disagree with what the person confirmed (spec §4).
 * These helpers only mirror that rule so the screen does not offer a button
 * the server would refuse. Keep them in step with the 422 branch of the
 * controller.
 */

const LIBRO_KINDS: readonly AvanceRowKind[] = ['libro_ventas', 'libro_compras', 'libro_guias'];

export function isLibroKind(kind: AvanceRowKind): boolean {
  return LIBRO_KINDS.includes(kind);
}

/**
 * Which step's «Declarar avance» a screen is about. The SII offers one PASO's
 * rows at a time — the ten Set de Pruebas rows, then the one «SET DE
 * SIMULACION» row — and the API returns every kind, so each screen keeps its
 * own.
 */
export type AvanceScope = 'test_set' | 'simulation';

/**
 * The view as one step's screen sees it: only its rows, and the last
 * declaration only when it was of that step. Without the second half the
 * Simulación screen opened on the Set de Pruebas' «Declaración enviada» card.
 *
 * The open declaration is kept whatever its step: there is only ever one per
 * company, and it blocks every other declaration until it ends.
 */
export function scopeAvanceView<T extends Pick<SiiAvanceView, 'rows' | 'lastDeclaration'>>(
  view: T,
  scope: AvanceScope,
): T {
  const simulation = scope === 'simulation';
  const lastStep = view.lastDeclaration?.step;
  return {
    ...view,
    rows: view.rows.filter((row) => (row.kind === 'simulacion') === simulation),
    lastDeclaration:
      lastStep !== undefined && (lastStep === 'simulacion') === simulation ? view.lastDeclaration : null,
  };
}

/**
 * The rows the portal form actually offers, in portal order.
 *
 * A `not_offered` row is a set the SII never gave this company: it has no
 * `NUM_ENVn` on the form, so it is not a row of the table (spec §7.1, "one row
 * per form row"). The screen mentions those separately.
 */
export function offeredRows(rows: readonly AvanceRow[]): AvanceRow[] {
  return rows.filter((row) => !row.reasons.includes('not_offered'));
}

export function notOfferedRows(rows: readonly AvanceRow[]): AvanceRow[] {
  return rows.filter((row) => row.reasons.includes('not_offered'));
}

/** The rows a declaration would send: the eligible offered ones. */
export function rowsToDeclare(rows: readonly AvanceRow[]): AvanceRow[] {
  return offeredRows(rows).filter((row) => row.eligible);
}

export type DeclarabilityBlock =
  /** A declaration is already queued or running. */
  | 'open_declaration'
  /**
   * No portal run can be attempted for this company right now (the automation
   * is off, no enabled representative, no valid certificate). The server's own
   * sentence, `unavailableReason`, is what the screen shows for it.
   */
  | 'unavailable'
  /** The form offers no row at all (nothing read yet, or nothing assigned). */
  | 'nothing_offered'
  /** No offered row is eligible. */
  | 'none_eligible'
  /** No offered row is eligible because the SII approved every one: nothing is left to declare. */
  | 'all_approved'
  /** Some offered rows are not eligible and partial declarations are off (spec §0 V2). */
  | 'not_all_eligible'
  /** The rows look declarable here, but the server offered no hash to confirm. */
  | 'no_payload';

/**
 * Whether «Declarar avance ante el SII» may be pressed, and if not, why.
 *
 * The rule mirrors the server's: every offered row eligible (a set the SII
 * already approved aside), or — only with `allowPartial` — at least one. An open declaration blocks everything, like
 * the partial unique index does, and so does an `unavailableReason`, which the
 * server answers with a 400 before looking at the rows. A null `payloadHash` is
 * the server saying "nothing to confirm": without a hash there is nothing the
 * POST could carry, so it blocks even when the rows alone would pass.
 */
/**
 * Whether the SII approved every set it offers: the step's declaring is done.
 * «Ningún set se puede declarar todavía» is wrong then (it says to wait for
 * something) and so is «Revisa el motivo de cada fila» (no row has a motive),
 * which is how the disabled button read once soak cycle 34 was fully approved.
 */
export function allOfferedApproved(rows: readonly AvanceRow[]): boolean {
  const offered = offeredRows(rows);
  return offered.length > 0 && offered.every((row) => row.siiEstadoClass === 'approved');
}

export function declarability(
  view: Pick<
    SiiAvanceView,
    'rows' | 'allowPartial' | 'openDeclaration' | 'payloadHash' | 'unavailableReason'
  >,
): { declarable: true } | { declarable: false; block: DeclarabilityBlock } {
  if (view.openDeclaration) return { declarable: false, block: 'open_declaration' };
  if (view.unavailableReason !== null) return { declarable: false, block: 'unavailable' };
  const offered = offeredRows(view.rows);
  if (offered.length === 0) return { declarable: false, block: 'nothing_offered' };
  const eligible = offered.filter((row) => row.eligible);
  if (eligible.length === 0) {
    return { declarable: false, block: allOfferedApproved(view.rows) ? 'all_approved' : 'none_eligible' };
  }
  // A set the SII already approved is done and never holds the others back.
  const pending = offered.filter((row) => row.siiEstadoClass !== 'approved');
  if (eligible.length < pending.length && !view.allowPartial) {
    return { declarable: false, block: 'not_all_eligible' };
  }
  if (view.payloadHash === null) return { declarable: false, block: 'no_payload' };
  return { declarable: true };
}

/**
 * Whether any set of the CURRENT Set de Pruebas has a run accepted in full.
 *
 * This is what decides where step 1 of the stepper links to: with nothing
 * accepted yet the next thing to do is emit (the «Historial» tab), not declare. It is
 * read off the rows because that is data the API already returns: a DTE row
 * (not a libro) with a run, and none of the reasons that mean "this run is not
 * an accepted run of the current set" (`no_candidate`, `superseded_set`,
 * `not_accepted`). Any other reason — the date window, a split envío, already
 * declared — still means a set was accepted.
 */
export function hasAcceptedSetRun(rows: readonly AvanceRow[]): boolean {
  return rows.some(
    (row) =>
      !isLibroKind(row.kind) &&
      row.kind !== 'simulacion' &&
      row.runId !== null &&
      !row.reasons.includes('no_candidate') &&
      !row.reasons.includes('superseded_set') &&
      !row.reasons.includes('not_accepted'),
  );
}

/** Where to go to fix a row: the run in «Historial», the libro in Libros, or the simulación. */
export function fixLinkFor(row: AvanceRow): { to: string; label: string } {
  if (row.kind === 'simulacion') {
    return {
      to: row.runId ? `/certification/simulation/runs?id=${row.runId}` : '/certification/simulation',
      label: 'Ir a la simulación',
    };
  }
  if (isLibroKind(row.kind)) {
    return {
      to:
        row.sourceType === 'libro' && row.sourceId
          ? `/certification/test-set/libros?id=${row.sourceId}`
          : '/certification/test-set/libros',
      label: 'Ir a Libros',
    };
  }
  return {
    to: row.runId ? `/certification/test-set/runs?id=${row.runId}` : '/certification/test-set/runs',
    label: 'Ir al historial',
  };
}

/**
 * «leído hace <n> min»: how long ago the SII was read, in the unit a person
 * would say it. Minutes up to an hour, hours up to two days, then days.
 */
export function formatReadAgo(readAtIso: string, now: Date = new Date()): string {
  const readAt = new Date(readAtIso).getTime();
  if (Number.isNaN(readAt)) return 'hace un momento';
  const minutes = Math.max(0, Math.floor((now.getTime() - readAt) / 60_000));
  if (minutes < 1) return 'hace menos de 1 min';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `hace ${hours} h`;
  return `hace ${Math.floor(hours / 24)} días`;
}

/** The aggregates the backend announces `sii_avance.changed` for. */
const AVANCE_AGGREGATES: ReadonlySet<string> = new Set([
  'sii_avance_declaration',
  'sii_avance_snapshot',
  'sii_avance_step_advance',
  'sii_test_set_download',
]);

/**
 * Whether a realtime frame is news for this company's «Avance SII» view: a
 * declaration that moved, or a «Ver Avance» snapshot that landed (spec §6.1).
 * The stream carries every company the actor holds, so the company is checked.
 */
export function isAvanceFrameFor(
  frame: Pick<RealtimeFrame, 'aggregateType' | 'companyId'>,
  companyId: string,
): boolean {
  return AVANCE_AGGREGATES.has(frame.aggregateType) && frame.companyId === companyId;
}

/**
 * Whether the SII itself (not Factit's estimate) has the company on a step
 * after `step`. The SII only moves a postulación forward once it approved the
 * step, and from then on «Ver Avance» stops listing that step's rows: on
 * 2026-11-04 the simulación read «REVISADO CONFORME», PASO turned
 * INTERCAMBIO DE INFORMACION, and the Simulación screen went on saying «el SII
 * está revisando tu set» over an empty table.
 */
export function stepPassedAtSii(
  stage: Pick<SiiAvanceView['stage'], 'current' | 'source'>,
  step: AvanceDeclaration['step'],
): boolean {
  return (
    stage.source === 'sii' &&
    CERTIFICATION_STAGES.indexOf(stage.current) > CERTIFICATION_STAGES.indexOf(step)
  );
}

/**
 * The SII's PASO names, as «Ver Avance» prints them. Mirrors the backend's
 * `PASO_TO_STAGE`; the SPA needs it to place a step advance, which carries the
 * PASO it left as text rather than as a stage.
 */
const PASO_STAGES: Record<string, CertificationStage> = {
  'SET DE PRUEBAS': 'set_de_pruebas',
  SIMULACION: 'simulacion',
  'INTERCAMBIO DE INFORMACION': 'intercambio',
};

/** The stage a PASO names, after normalizing case, accents and spaces; null when unknown. */
export function stageOfPaso(paso: string | null | undefined): CertificationStage | null {
  if (!paso) return null;
  const key = paso
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
  return PASO_STAGES[key] ?? null;
}

/**
 * Whether the SII has moved the company past the step a step advance landed
 * on, so the advance is history. «Avanzaste desde el paso SET DE PRUEBAS»,
 * with its «ha pasado al paso SIMULACION», stayed under «Ahora: Intercambio de
 * información» (2026-11-03), reading as if the company were still between the
 * first two steps.
 *
 * A press that did not go through never moved the company, so it is history
 * once the SII shows the company past the PASO it tried to leave.
 */
export function advanceSuperseded(
  advance: Pick<AvanceStepAdvance, 'paso' | 'status'>,
  stage: Pick<SiiAvanceView['stage'], 'current' | 'source'> | undefined,
): boolean {
  if (!stage || stage.source !== 'sii') return false;
  const left = stageOfPaso(advance.paso);
  if (left === null) return false;
  const reference = CERTIFICATION_STAGES.indexOf(left) + (advance.status === 'submitted' ? 1 : 0);
  return CERTIFICATION_STAGES.indexOf(stage.current) > reference;
}

/**
 * What the SII decided about the last submitted declaration, read off the
 * snapshot through the rows' `siiEstadoClass`.
 */
export type DeclarationVerdict =
  | { kind: 'rejected'; rejected: AvanceRow[]; declaredCount: number }
  | { kind: 'approved'; declaredCount: number };

/**
 * `rejected` as soon as the SII rejected one declared row, `approved` once it
 * approved every one, and null while it is still reviewing — or while the
 * latest snapshot predates the declaration, because then its estados are the
 * previous round's, not this one's.
 *
 * The «Declaración enviada» card quotes the portal's answer at submission,
 * which always reads EN REVISION; without this the screen kept saying so
 * for two weeks after the SII had rejected all ten rows (2026-10-12).
 */
export function declarationVerdict(
  view: Pick<SiiAvanceView, 'rows' | 'snapshot' | 'lastDeclaration'> &
    Partial<Pick<SiiAvanceView, 'stage'>>,
): DeclarationVerdict | null {
  const declaration = view.lastDeclaration;
  if (!declaration || declaration.status !== 'submitted') return null;
  // The SII moved the company past the declared step: that is its approval,
  // even though «Ver Avance» no longer lists the step's rows to read it from.
  if (view.stage && stepPassedAtSii(view.stage, declaration.step)) {
    return { kind: 'approved', declaredCount: declaration.rows.length };
  }
  if (!view.snapshot) return null;
  const submittedAt = new Date(declaration.finishedAt ?? declaration.createdAt).getTime();
  if (new Date(view.snapshot.readAt).getTime() < submittedAt) return null;

  const bySetCode = new Map(view.rows.map((row) => [row.setCode, row]));
  const declared = declaration.rows
    .map((row) => bySetCode.get(row.setCode))
    .filter((row): row is AvanceRow => row !== undefined);
  if (declared.length === 0) return null;

  const rejected = declared.filter((row) => row.siiEstadoClass === 'rejected');
  if (rejected.length > 0) {
    return { kind: 'rejected', rejected, declaredCount: declaration.rows.length };
  }
  const allApproved =
    declared.length === declaration.rows.length &&
    declared.every((row) => row.siiEstadoClass === 'approved');
  return allApproved ? { kind: 'approved', declaredCount: declaration.rows.length } : null;
}

/**
 * Whether a declaration was made with the envíos of the CURRENT Set de
 * Pruebas: every row it declared still carries the same trackId today.
 *
 * A new set download replaces every number, so a declaration of the previous
 * set declares envíos the SII no longer evaluates. Shown as «Declaración
 * enviada al SII» over a fresh set whose rows all read «Por realizar», it read
 * as if the new set were already declared (2026-10-24).
 */
export function declarationIsForCurrentSet(
  declaration: Pick<AvanceDeclaration, 'rows'>,
  rows: readonly AvanceRow[],
): boolean {
  // Nothing declared contradicts the current set: a declaration that failed
  // before recording its rows is still about this one.
  if (declaration.rows.length === 0) return true;
  const trackIdOf = new Map(rows.map((row) => [row.setCode, row.trackId]));
  return declaration.rows.every((row) => trackIdOf.get(row.setCode) === row.trackId);
}

/**
 * Where the current Set de Pruebas stands against the one question the screens
 * that are not «Declarar avance» get asked: can it be declared yet?
 *
 * - `declaring`: a declaration is queued or running.
 * - `declared`: this set was declared; `verdict` is what the SII decided, or
 *   null while it reviews.
 * - `ready`: «Declarar avance ante el SII» would go through.
 * - `not_ready`: it would not; `pending` are the offered rows still to be
 *   done (neither eligible nor already approved), in portal order.
 *
 * It reads the same rules as the declare button ({@link declarability}), so the
 * summary cannot promise what the button then refuses.
 */
export type AvanceReadiness =
  | { kind: 'declaring' }
  | { kind: 'declared'; declaredAt: string; verdict: DeclarationVerdict | null }
  | { kind: 'ready'; count: number }
  | {
      kind: 'not_ready';
      done: number;
      total: number;
      pending: AvanceRow[];
      block: DeclarabilityBlock;
    };

export function avanceReadiness(
  view: Pick<
    SiiAvanceView,
    'rows' | 'allowPartial' | 'openDeclaration' | 'payloadHash' | 'unavailableReason' | 'snapshot' | 'lastDeclaration'
  >,
): AvanceReadiness {
  if (view.openDeclaration) return { kind: 'declaring' };
  const last = view.lastDeclaration;
  if (last && last.status === 'submitted' && declarationIsForCurrentSet(last, view.rows)) {
    return {
      kind: 'declared',
      declaredAt: last.finishedAt ?? last.createdAt,
      verdict: declarationVerdict(view),
    };
  }
  const decision = declarability(view);
  const offered = offeredRows(view.rows);
  if (decision.declarable) return { kind: 'ready', count: rowsToDeclare(view.rows).length };
  const isDone = (row: AvanceRow) => row.eligible || row.siiEstadoClass === 'approved';
  return {
    kind: 'not_ready',
    done: offered.filter(isDone).length,
    total: offered.length,
    pending: offered.filter((row) => !isDone(row)),
    block: decision.block,
  };
}
